import { useEffect, useMemo, useState } from "react";
import { api, apiUrl, fmtINR, fmtCompact } from "@/lib/api";
import { PageHeader, Section, Btn, Modal, Input, Select, Badge, MonthPicker } from "@/components/Shared";
import { Plus, Pencil, Trash2, Download, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { openConfirm } from "@/lib/dialogs";

const EMPTY = { gateway: "", period_label: "", invoice_month: "", commission: 0, tds: 0, final_commission: 0, gst_total: 0, gst_paid_status: "Pending", funds_received_status: "Pending", invoice_no: "", invoice_amount: 0, granularity: "invoice" };

export default function PGEarnings() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ by_gateway: [], totals: {}, available_fys: [] });
  const [gateways, setGateways] = useState([]);
  const [fyFilter, setFyFilter] = useState("");
  const [gwFilter, setGwFilter] = useState("");
  const [q, setQ] = useState("");
  // Filters added Feb 2026 — admin needs to slice by invoice lifecycle stage
  // (e.g. "all April invoices where funds are still pending") without
  // scrolling/CSV-export. Pure client-side derivations off the loaded rows.
  const [monthFilter, setMonthFilter] = useState("");
  const [gstFilter, setGstFilter] = useState("");
  const [fundsFilter, setFundsFilter] = useState("");
  const [sortBy, setSortBy] = useState({ key: "invoice_month", dir: "desc" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [duplicates, setDuplicates] = useState({ duplicates: [], total_groups: 0 });
  const [showDupes, setShowDupes] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkGstStatus, setBulkGstStatus] = useState("");
  const [bulkFundsStatus, setBulkFundsStatus] = useState("");

  const load = async () => {
    const [list, sum, gws, dupes] = await Promise.all([
      api.get(`/pg-earnings${fyFilter ? `?fy=${encodeURIComponent(fyFilter)}` : ""}`),
      api.get(`/pg-earnings/summary${fyFilter ? `?fy=${encodeURIComponent(fyFilter)}` : ""}`),
      api.get("/gateways"),
      api.get("/pg-earnings/duplicates"),
    ]);
    setRows(list.data || []);
    setSummary(sum.data);
    setGateways(gws.data || []);
    setDuplicates(dupes.data || { duplicates: [], total_groups: 0 });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fyFilter]);

  const save = async () => {
    // Mandatory fields — block save with a friendly toast instead of relying
    // on the backend's 422 (which would surface a less-clear error).
    const missing = [];
    if (!String(form.gateway || "").trim()) missing.push("Gateway");
    if (!String(form.invoice_month || "").trim()) missing.push("Invoice Month");
    if (!Number(form.commission)) missing.push("Commission");
    if (missing.length) {
      toast.error(`Please fill: ${missing.join(", ")}`);
      return;
    }
    try {
      const payload = { ...form, commission: Number(form.commission), tds: Number(form.tds), final_commission: Number(form.final_commission), gst_total: Number(form.gst_total), invoice_amount: Number(form.invoice_amount) };
      if (editId) await api.put(`/pg-earnings/${editId}`, payload);
      else await api.post("/pg-earnings", payload);
      toast.success("Saved"); setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!await openConfirm({ title: "Delete earning row?", message: "This invoice/commission entry will be removed and won't appear in future PG earning reports.", confirmLabel: "Delete row", danger: true })) return;
    await api.delete(`/pg-earnings/${id}`); toast.success("Deleted"); load();
  };

  // Cycle GST paid status with a single click on the badge:
  //   Pending → YES (Paid) → NA → Pending …
  // Uses the existing PUT endpoint so a full audit log is kept (same as the
  // edit-modal save path).
  const toggleGstStatus = async (row) => {
    const cycle = { Pending: "YES", YES: "NA", NA: "Pending", Paid: "NA" };
    const next = cycle[row.gst_paid_status] || "Pending";
    try {
      await api.put(`/pg-earnings/${row.id}`, { ...row, gst_paid_status: next });
      toast.success(`GST status → ${next}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (e?.message || "Failed"));
    }
  };

  // Funds-received toggle: same one-click cycle as GST but on the
  // funds_received_status field. Tracks whether the post-deduction (commission
  // − TDS) money has actually landed from the gateway.
  //   Pending → Received → Pending …
  const toggleFundsStatus = async (row) => {
    const cur = row.funds_received_status || "Pending";
    const next = cur === "Received" ? "Pending" : "Received";
    try {
      await api.put(`/pg-earnings/${row.id}`, { ...row, funds_received_status: next });
      toast.success(`Funds → ${next}`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (e?.message || "Failed"));
    }
  };
  const openEdit = (r) => { setForm({ ...r }); setEditId(r.id); setOpen(true); };
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };

  // Auto-calc TDS (2%), GST (18%), final commission + invoice amount from commission.
  // User can still override — only recomputes when the Commission field itself changes.
  const onCommissionChange = (raw) => {
    const cnum = Number(raw || 0);
    const tds = +(cnum * 0.02).toFixed(2);
    const finalC = +(cnum - tds).toFixed(2);
    const gst = +(cnum * 0.18).toFixed(2);
    const invAmt = +(finalC + gst).toFixed(2);
    setForm((f) => ({ ...f, commission: raw, tds, final_commission: finalC, gst_total: gst, invoice_amount: invAmt }));
  };

  // On gateway select: auto-suggest the next invoice number
  const onGatewayChange = async (v) => {
    setForm((f) => ({ ...f, gateway: v }));
    if (!v || editId) return;
    try {
      const r = await api.get(`/pg-earnings/next-invoice-no?gateway=${encodeURIComponent(v)}`);
      if (r.data?.suggestion) {
        setForm((f) => ({ ...f, gateway: v, invoice_no: r.data.suggestion }));
      }
    } catch (e) {
      // Phase 1 — auto-suggest is a nice-to-have; if the backend can't
      // produce a suggestion, the user can type the invoice number
      // manually. Log to devtools for debuggability.
      console.error("PGEarnings: invoice suggestion fetch failed", e);
    }
  };
  const toggleSort = (key) => setSortBy((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });
  const SortHeader = ({ k, children, align = "left" }) => {
    const active = sortBy.key === k;
    const Icon = !active ? ArrowUpDown : (sortBy.dir === "asc" ? ArrowUp : ArrowDown);
    return (
      <th className={align === "right" ? "text-right" : ""}>
        <button type="button" data-testid={`pge-sort-${k}`} onClick={() => toggleSort(k)}
          className={`inline-flex items-center gap-1 hover:text-[var(--ink)] ${active ? "text-[var(--ink)]" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}>
          <span>{children}</span>
          <Icon size={11} strokeWidth={2} className={active ? "text-[var(--accent)]" : "text-[var(--muted)]"} />
        </button>
      </th>
    );
  };

  const filtered = useMemo(() => {
    let list = rows.filter((r) => {
      if (gwFilter && r.gateway !== gwFilter) return false;
      if (monthFilter && r.invoice_month !== monthFilter) return false;
      if (gstFilter) {
        // Normalise legacy YES/Paid into a single "Paid" bucket so the
        // filter behaves predictably across old + new rows.
        const cur = r.gst_paid_status === "YES" ? "Paid" : (r.gst_paid_status || "Pending");
        if (cur !== gstFilter) return false;
      }
      if (fundsFilter && (r.funds_received_status || "Pending") !== fundsFilter) return false;
      if (q && !`${r.gateway} ${r.invoice_no} ${r.period_label}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
    const { key, dir } = sortBy;
    const mul = dir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      const va = a[key], vb = b[key];
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * mul;
      return String(va || "").localeCompare(String(vb || "")) * mul;
    });
    return list;
  }, [rows, gwFilter, monthFilter, gstFilter, fundsFilter, q, sortBy]);

  const t = summary.totals || {};
  // Pending funds = total final_commission across rows whose
  // funds_received_status is anything other than "Received". Computed
  // client-side so the KPI always reflects the latest row state without
  // waiting for the summary endpoint to refresh.
  const pendingFunds = (rows || []).reduce((acc, r) => {
    const status = (r.funds_received_status || "Pending");
    return status === "Received" ? acc : acc + (Number(r.final_commission) || 0);
  }, 0);
  const pendingCount = (rows || []).filter((r) => (r.funds_received_status || "Pending") !== "Received").length;
  const hasFilters = gwFilter || q || monthFilter || gstFilter || fundsFilter;
  const clearAllFilters = () => { setGwFilter(""); setQ(""); setMonthFilter(""); setGstFilter(""); setFundsFilter(""); };
  // Build dropdown options off the loaded rows so we never offer a value
  // that has zero matches.
  const monthOptions = useMemo(() => {
    const ms = (rows || []).map((r) => r.invoice_month).filter(Boolean);
    return Array.from(new Set(ms)).sort().reverse();
  }, [rows]);

  // Bulk selection helpers
  const visibleIds = useMemo(() => filtered.map((r) => r.id), [filtered]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0 && !allSelected;
  const toggleOne = (id) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (allSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkSetGst = async (status) => {
    if (!selectedIds.size) return;
    try {
      const r = await api.patch("/pg-earnings/bulk", { ids: [...selectedIds], patch: { gst_paid_status: status } });
      toast.success(`Updated ${r.data.modified} rows → GST ${status}`);
      setBulkGstStatus(""); clearSelection(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const bulkSetFunds = async (status) => {
    if (!selectedIds.size) return;
    try {
      const r = await api.patch("/pg-earnings/bulk", { ids: [...selectedIds], patch: { funds_received_status: status } });
      toast.success(`Updated ${r.data.modified} rows → Funds ${status}`);
      setBulkFundsStatus(""); clearSelection(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const bulkDelete = async () => {
    if (!selectedIds.size) return;
    if (!await openConfirm({
      title: `Delete ${selectedIds.size} invoice${selectedIds.size === 1 ? "" : "s"}?`,
      message: "Selected invoice rows will be permanently deleted. This cannot be undone.",
      confirmLabel: `Delete ${selectedIds.size} invoice${selectedIds.size === 1 ? "" : "s"}`,
      danger: true,
    })) return;
    try {
      const r = await api.post("/pg-earnings/bulk-delete", { ids: [...selectedIds] });
      toast.success(`Deleted ${r.data.deleted} rows`);
      clearSelection(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="pg-earnings-page">
      <PageHeader title="PG Earnings" actions={<>
        <div className="w-36"><Select testid="pge-fy-filter" placeholder="All FYs" options={summary.available_fys || []} value={fyFilter} onChange={setFyFilter} /></div>
        <Btn variant="accent" testid="pge-export" onClick={() => window.open(apiUrl(`/export/pg-earnings${fyFilter ? `?fy=${encodeURIComponent(fyFilter)}` : ""}`), "_blank")}><Download size={14} /> Export CSV</Btn>
        <Btn variant="primary" testid="pge-add" onClick={openNew}><Plus size={14} /> Add Invoice</Btn>
      </>} />
      <div className="p-4 sm:p-10 space-y-6">
        {duplicates.total_groups > 0 && (
          <div className="bg-amber-50 border border-amber-300 p-4 rounded-sm" data-testid="pge-dupe-banner">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-amber-800 font-semibold flex items-center gap-1.5">
                  <span className="inline-block w-1.5 h-1.5 bg-amber-500 rounded-full" />
                  Duplicate invoice numbers detected
                </div>
                <div className="text-[13px] text-amber-900 mt-1">
                  {duplicates.total_groups} invoice number{duplicates.total_groups === 1 ? "" : "s"} appear on {duplicates.total_rows} rows. Invoice numbers must be unique across all gateways — please edit or delete the extras.
                </div>
              </div>
              <Btn variant="ghost" onClick={() => setShowDupes((v) => !v)} testid="pge-dupe-toggle">{showDupes ? "Hide" : "Show details"}</Btn>
            </div>
            {showDupes && (
              <div className="mt-3 pt-3 border-t border-amber-200">
                <table className="table-data w-full text-[12px]">
                  <thead><tr><th>Invoice #</th><th>Gateway</th><th>Period</th><th>Month</th><th className="text-right">Commission</th><th></th></tr></thead>
                  <tbody>
                    {duplicates.duplicates.flatMap((g) => g.rows.map((r, i) => (
                      <tr key={r.id} data-testid={`pge-dupe-row-${r.id}`}>
                        <td className="font-mono font-semibold">{i === 0 ? g.invoice_no : ""}</td>
                        <td>{r.gateway}</td>
                        <td>{r.period_label}</td>
                        <td className="num">{r.invoice_month || "—"}</td>
                        <td className="text-right num">{fmtINR(r.commission, 0)}</td>
                        <td className="text-right">
                          <button onClick={() => { const full = rows.find((x) => x.id === r.id); if (full) openEdit(full); }} className="p-1 text-amber-700 hover:bg-amber-100 rounded-sm"><Pencil size={12} /></button>
                          <button onClick={() => remove(r.id)} className="p-1 text-[var(--negative)] hover:bg-red-50 rounded-sm"><Trash2 size={12} /></button>
                        </td>
                      </tr>
                    )))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Gross Commission</div><div className="num text-xl font-semibold mt-1.5">₹ {fmtCompact(t.commission || 0)}</div></div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">TDS</div><div className="num text-xl font-semibold mt-1.5 text-[var(--negative)]">₹ {fmtCompact(t.tds || 0)}</div></div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Final Commission</div><div className="num text-xl font-semibold mt-1.5 text-[var(--positive)]">₹ {fmtCompact(t.final_commission || 0)}</div></div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">GST Collected</div><div className="num text-xl font-semibold mt-1.5">₹ {fmtCompact(t.gst_total || 0)}</div></div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Invoice Total</div><div className="num text-xl font-semibold mt-1.5 text-[var(--accent)]">₹ {fmtCompact(t.invoice_amount || 0)}</div></div>
          <div className="bg-amber-50/60 border border-amber-300 p-4" data-testid="pge-pending-funds-kpi">
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-900 font-semibold">Pending Funds</div>
            <div className="num text-xl font-semibold mt-1.5 text-amber-900">₹ {fmtCompact(pendingFunds)}</div>
            <div className="text-[10.5px] text-amber-800/80 mt-0.5 num">
              {pendingCount === 0 ? "All received" : `${pendingCount} invoice${pendingCount === 1 ? "" : "s"} outstanding`}
            </div>
          </div>
        </div>

        <Section title="By Payment Gateway" testid="pge-by-gateway">
          <div className="overflow-x-auto"><table className="table-data w-full">
            <thead><tr><th>Gateway</th><th className="text-right">Rows</th><th className="text-right">Commission</th><th className="text-right">TDS</th><th className="text-right">Final</th><th className="text-right">GST</th><th className="text-right">Invoice Total</th></tr></thead>
            <tbody>
              {(summary.by_gateway || []).map((g) => (
                <tr key={g.gateway} data-testid={`pge-gw-${g.gateway}`}>
                  <td className="font-medium">{g.gateway}</td>
                  <td className="text-right num">{g.rows}</td>
                  <td className="text-right num">{fmtINR(g.commission, 0)}</td>
                  <td className="text-right num text-[var(--negative)]">{fmtINR(g.tds, 0)}</td>
                  <td className="text-right num text-[var(--positive)] font-semibold">{fmtINR(g.final_commission, 0)}</td>
                  <td className="text-right num">{fmtINR(g.gst_total, 0)}</td>
                  <td className="text-right num text-[var(--accent)]">{fmtINR(g.invoice_amount, 0)}</td>
                </tr>
              ))}
              {!summary.by_gateway?.length && <tr><td colSpan={7} className="text-center text-[var(--muted)] py-6">No earnings yet.</td></tr>}
            </tbody>
          </table></div>
        </Section>

        <div className="flex flex-wrap gap-2 items-center" data-testid="pge-filter-bar">
          <div className="w-64"><Input placeholder="Search invoice / gateway / period…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pge-search" /></div>
          <div className="w-44"><Select testid="pge-gateway-filter" placeholder="All gateways" options={[...new Set(rows.map((r) => r.gateway))].sort()} value={gwFilter} onChange={setGwFilter} /></div>
          <div className="w-40"><Select testid="pge-month-filter" placeholder="All months" options={monthOptions} value={monthFilter} onChange={setMonthFilter} /></div>
          <div className="w-36"><Select testid="pge-gst-filter" placeholder="GST: any" options={["Paid", "Pending", "NA"]} value={gstFilter} onChange={setGstFilter} /></div>
          <div className="w-36"><Select testid="pge-funds-filter" placeholder="Funds: any" options={["Pending", "Received"]} value={fundsFilter} onChange={setFundsFilter} /></div>
          {hasFilters && <Btn variant="ghost" testid="pge-clear-filters" onClick={clearAllFilters}>Clear filters</Btn>}
        </div>

        <Section title={`Invoices — ${filtered.length} row${filtered.length === 1 ? "" : "s"}`} testid="pge-invoices">
          {selectedIds.size > 0 && (
            <div className="sticky top-0 z-10 px-4 py-2.5 bg-[var(--ink)] text-white flex items-center gap-3 border-b border-[var(--line-2)]" data-testid="pge-bulk-bar">
              <div className="text-[12px] font-semibold"><span className="num">{selectedIds.size}</span> selected</div>
              <div className="h-4 w-px bg-white/20" />
              <span className="text-[11px] text-white/70 uppercase tracking-[0.14em] font-semibold">GST Paid?</span>
              <div className="w-28">
                <select
                  data-testid="pge-bulk-gst-status"
                  value={bulkGstStatus}
                  onChange={(e) => { const v = e.target.value; setBulkGstStatus(v); if (v) bulkSetGst(v); }}
                  className="h-8 w-full px-2 text-[12px] bg-white text-[var(--ink)] border border-white rounded-sm focus:outline-none"
                >
                  <option value="">Set to…</option>
                  <option value="YES">YES</option>
                  <option value="Pending">Pending</option>
                  <option value="NA">NA</option>
                </select>
              </div>
              <span className="text-[11px] text-white/70 uppercase tracking-[0.14em] font-semibold">Funds?</span>
              <div className="w-32">
                <select
                  data-testid="pge-bulk-funds-status"
                  value={bulkFundsStatus}
                  onChange={(e) => { const v = e.target.value; setBulkFundsStatus(v); if (v) bulkSetFunds(v); }}
                  className="h-8 w-full px-2 text-[12px] bg-white text-[var(--ink)] border border-white rounded-sm focus:outline-none"
                >
                  <option value="">Set to…</option>
                  <option value="Received">Received</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
              <button onClick={bulkDelete} data-testid="pge-bulk-delete" className="h-8 px-3 text-[12px] font-semibold bg-[var(--negative)] text-white rounded-sm hover:bg-red-700 flex items-center gap-1.5">
                <Trash2 size={12} /> Delete
              </button>
              <div className="flex-1" />
              <button onClick={clearSelection} data-testid="pge-bulk-clear" className="text-[12px] text-white/80 hover:text-white underline-offset-2 hover:underline">Clear</button>
            </div>
          )}
          <div className="overflow-auto max-h-[600px]">
            <div className="overflow-x-auto"><table className="table-data w-full">
              <thead><tr>
                <th className="w-8 pl-3">
                  <input
                    type="checkbox"
                    data-testid="pge-select-all"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAllVisible}
                    className="cursor-pointer accent-[var(--accent)]"
                  />
                </th>
                <SortHeader k="gateway">Gateway</SortHeader>
                <SortHeader k="period_label">Period</SortHeader>
                <SortHeader k="invoice_month">Month</SortHeader>
                <SortHeader k="invoice_no">Invoice #</SortHeader>
                <SortHeader k="commission" align="right">Commission</SortHeader>
                <SortHeader k="tds" align="right">TDS</SortHeader>
                <SortHeader k="final_commission" align="right">Final</SortHeader>
                <SortHeader k="gst_total" align="right">GST</SortHeader>
                <SortHeader k="invoice_amount" align="right">Invoice Amt</SortHeader>
                <th>GST Paid?</th>
                <th>Funds Received?</th>
                <th></th>
              </tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`pge-row-${r.id}`} className={selectedIds.has(r.id) ? "bg-[var(--accent)]/5" : ""}>
                    <td className="pl-3">
                      <input
                        type="checkbox"
                        data-testid={`pge-select-${r.id}`}
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleOne(r.id)}
                        className="cursor-pointer accent-[var(--accent)]"
                      />
                    </td>
                    <td className="font-medium">{r.gateway}</td>
                    <td className="text-[12px]">{r.period_label}</td>
                    <td className="num text-[12px]">{r.invoice_month}</td>
                    <td className="num text-[12px]">{r.invoice_no || "—"}</td>
                    <td className="text-right num">{fmtINR(r.commission, 0)}</td>
                    <td className="text-right num text-[var(--negative)]">{fmtINR(r.tds, 0)}</td>
                    <td className="text-right num text-[var(--positive)]">{fmtINR(r.final_commission, 0)}</td>
                    <td className="text-right num">{fmtINR(r.gst_total, 0)}</td>
                    <td className="text-right num text-[var(--accent)]">{fmtINR(r.invoice_amount, 0)}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => toggleGstStatus(r)}
                        data-testid={`pge-gst-toggle-${r.id}`}
                        title="Click to cycle status: Pending → Paid → NA → Pending"
                        className="cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[var(--accent)] rounded-sm"
                      >
                        <Badge tone={r.gst_paid_status === "YES" || r.gst_paid_status === "Paid" ? "positive" : (r.gst_paid_status === "NA" ? "muted" : "warning")}>{r.gst_paid_status}</Badge>
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => toggleFundsStatus(r)}
                        data-testid={`pge-funds-toggle-${r.id}`}
                        title="Click to toggle: Pending ↔ Received"
                        className="cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[var(--accent)] rounded-sm"
                      >
                        <Badge tone={r.funds_received_status === "Received" ? "positive" : "warning"}>{r.funds_received_status || "Pending"}</Badge>
                      </button>
                    </td>
                    <td className="text-right">
                      <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm"><Pencil size={13} /></button>
                      <button onClick={() => remove(r.id)} className="p-1.5 hover:bg-red-50 text-[var(--negative)] rounded-sm"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
                {!filtered.length && <tr><td colSpan={13} className="text-center text-[var(--muted)] py-6">No invoices.</td></tr>}
              </tbody>
            </table></div>
          </div>
        </Section>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Invoice" : "Add Invoice"} testid="pge-modal">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Gateway <span className="text-[var(--negative)]">*</span></label>
            <Select testid="pge-form-gw" placeholder="Select gateway" options={gateways.map((g) => g.gateway_name)} value={form.gateway} onChange={onGatewayChange} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Period (free text)</label>
            <Input value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} placeholder="e.g. 1-5th April, Aug 2025" /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Invoice Month <span className="text-[var(--negative)]">*</span></label>
            <MonthPicker value={form.invoice_month} onChange={(v) => setForm({ ...form, invoice_month: v })} testid="pge-form-invoice-month" /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider flex items-center justify-between"><span>Invoice #</span>{!editId && form.gateway && form.invoice_no && <span className="text-[var(--muted)] normal-case tracking-normal text-[10px]">auto — editable</span>}</label>
            <Input data-testid="pge-form-invoice-no" value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} placeholder="BIZ-XXX-001" /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Commission <span className="text-[var(--negative)]">*</span></label>
            <Input data-testid="pge-form-commission" type="number" value={form.commission} onChange={(e) => onCommissionChange(e.target.value)} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider flex items-center justify-between"><span>TDS <span className="text-[var(--muted)] normal-case tracking-normal">(2% auto)</span></span></label>
            <Input type="number" value={form.tds} onChange={(e) => setForm({ ...form, tds: e.target.value })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider flex items-center justify-between"><span>Final Commission <span className="text-[var(--muted)] normal-case tracking-normal">(auto)</span></span></label>
            <Input type="number" value={form.final_commission} onChange={(e) => setForm({ ...form, final_commission: e.target.value })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider flex items-center justify-between"><span>GST Total <span className="text-[var(--muted)] normal-case tracking-normal">(18% auto)</span></span></label>
            <Input type="number" value={form.gst_total} onChange={(e) => setForm({ ...form, gst_total: e.target.value })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider flex items-center justify-between"><span>Invoice Amount <span className="text-[var(--muted)] normal-case tracking-normal">(auto)</span></span></label>
            <Input type="number" value={form.invoice_amount} onChange={(e) => setForm({ ...form, invoice_amount: e.target.value })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">GST Paid Status</label>
            <Select placeholder="—" options={["Pending", "YES", "NA"]} value={form.gst_paid_status} onChange={(v) => setForm({ ...form, gst_paid_status: v })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Funds Received Status</label>
            <Select placeholder="—" options={["Pending", "Received"]} value={form.funds_received_status || "Pending"} onChange={(v) => setForm({ ...form, funds_received_status: v })} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={save}>{editId ? "Save" : "Add"}</Btn>
        </div>
      </Modal>
    </div>
  );
}
